import { useEffect, useMemo } from 'react';
import { useEntitySchemaContext } from './EntitySchemaContext';
import {
  type EntitySchemaApiId,
  type EntitySchemaSlug,
  type EntitySchemaV1,
  type EntityType,
} from '@/model/EntitySchema.model';

interface UseEntitySchemaFieldsResponse {
  entityFields: Array<EntitySchemaV1>;
  entityLoading: boolean;
  entityUpdating: boolean;
  hasEntityError: boolean;
}

interface UseEntitySchemaActionsResponse {
  setFieldRequired: (apiId: EntitySchemaApiId, required: boolean) => Promise<boolean>;
  entityUpdating: boolean;
  hasEntityError: boolean;
}

export type EntityFieldRequiredMap = Record<EntitySchemaSlug, boolean>;

export const useEntitySchemaFields = (): UseEntitySchemaFieldsResponse => {
  const { entityFields, entityLoading, entityUpdating, hasEntityError } = useEntitySchemaContext();
  return {
    entityFields,
    entityLoading,
    entityUpdating,
    hasEntityError,
  };
};

export const useEntitySchemaActions = (): UseEntitySchemaActionsResponse => {
  const { setFieldRequired, entityUpdating, hasEntityError } = useEntitySchemaContext();
  return { setFieldRequired, entityUpdating, hasEntityError };
};

/**
 * Re-reads the entity schema when the calling component mounts. `EntitySchemaProvider` loads it once
 * per app session, so a required flag toggled in the glossary afterwards would otherwise stay stale.
 *
 * Call from a form or page root, not a per-row child: one request per mount. Forms that must not
 * render until the refresh lands should await `refreshFields()` in their own load (see `CreateAnimals`).
 */
export const useRefreshEntitySchemaOnMount = (): void => {
  const { refreshFields } = useEntitySchemaContext();

  useEffect(() => {
    void refreshFields();
  }, [refreshFields]);
};

export const useEntitySchemaFieldsByType = (entityType: EntityType): EntityFieldRequiredMap => {
  const { entityFields } = useEntitySchemaContext();

  return useMemo(
    () =>
      entityFields.reduce<EntityFieldRequiredMap>((acc, field) => {
        if (field.entity_type === entityType) {
          acc[field.slug] = field.required;
        }
        return acc;
      }, {} as EntityFieldRequiredMap),
    [entityFields, entityType]
  );
};
